import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { currentLocale } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommands, type EnqueueInput } from '@/offline'
import { useToast } from '@/ui'
import {
  Answer,
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Readout,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { V1Photo } from '@/features/gate/v1/photo'
import { C, typeKey } from '@/features/gate/v1/style'
import { fmtDate, tr } from '@/features/gate/v1/text'
import { STORAGE_FORM_CODE, type SignBayDeclarationPayload } from '../commands'
import { declarationInfo, siteOf, type StoreState } from '../projection'
import { declarationMissing } from '../rules'
import { useDraftState, useNow } from '../useStore'
import { DoneScreen, IconBox, NumberedLine, StoreHeader, WithStore } from './bits'

type Answers = Record<string, boolean | number | null>

interface FormDraft {
  answers: Answers
  photo_id: string | null
}

/**
 * V1-F5 — गोदाम का फ़ॉर्म: one signed paper for the whole godown. Its photo and answers are
 * recorded for every storage bay of the site (sign_bay_declaration per bay), so each bay's
 * declaration stays in force as the full build expects.
 */
export function V1Declaration() {
  const navigate = useNavigate()
  const home = () => navigate('/m/store')
  return <WithStore onBack={home}>{(state) => <FormFlow state={state} onHome={home} />}</WithStore>
}

/** The site's storage bays: the places a declaration covers. */
function storageBays(state: StoreState) {
  return [...state.locations.values()].filter(
    (l) =>
      l.level === 'BAY' &&
      l.active &&
      (l.role ?? 'STORAGE') === 'STORAGE' &&
      siteOf(state, l.id)?.id === state.siteId,
  )
}

function FormFlow({ state, onHome }: { state: StoreState; onHome: () => void }) {
  const actor = useActor()
  const toast = useToast()
  const now = useNow()
  const [draft, update, loaded, clear] = useDraftState<FormDraft>(
    `store.v1.form:${state.siteId}`,
    'store.v1.form',
    () => ({ answers: {}, photo_id: null }),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [metres, setMetres] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const form = state.form
  const bays = storageBays(state)
  const missing = declarationMissing(form, draft.answers)
  const validTo = now + state.cache.settings.declaration_renewal_days * 86_400_000
  const place = state.cache.site_name ?? ''
  // In force when every bay has a clean declaration: show until when (the earliest).
  const infos = bays.map((b) => declarationInfo(state, b.id, now))
  const inForceUntil =
    bays.length > 0 && infos.every((i) => i.status === 'CLEAN')
      ? infos
          .map((i) => i.declaration!.valid_to)
          .sort()
          .at(0)
      : null
  const setAnswer = (key: string, v: boolean | number | null) =>
    update((d) => ({ ...d, answers: { ...d.answers, [key]: v } }))

  const sign = async () => {
    if (!draft.photo_id || missing.length || bays.length === 0) return
    setSaving(true)
    try {
      const answers: Answers = {}
      for (const q of form.questions) answers[q.key] = draft.answers[q.key] ?? null
      const signedAt = new Date().toISOString()
      const cmds: EnqueueInput[] = bays.map((b) => {
        const payload: SignBayDeclarationPayload = {
          declaration: {
            id: newId(),
            subject_type: 'LOCATION',
            subject_id: b.id,
            form_code: STORAGE_FORM_CODE,
            form_version: form.version,
            answers,
            photo_id: draft.photo_id!,
            signed_at: signedAt,
          },
        }
        return {
          type: 'sign_bay_declaration',
          payload,
          actorId: actor.user_id,
          mediaIds: [draft.photo_id!],
        }
      })
      await enqueueCommands(cmds)
      setDone(new Date(validTo).toISOString())
      await clear()
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <DoneScreen
        title={tr('store.v1.signed.title', { place })}
        lines={tr('store.v1.signed.until', { date: fmtDate(done) })}
        chips={[{ icon: 'godown', text: tr('store.v1.signed.whole') }]}
        onHome={onHome}
      />
    )
  if (!loaded) return <V1Screen />
  const hi = currentLocale() === 'hi'

  return (
    <V1Screen
      header={<StoreHeader onBack={onHome} icon="shield" title={tr('store.v1.form.title')} />}
      footer={
        <Footer>
          {bays.length === 0 ? (
            <WaitCta>{tr('store.v1.form.noBays')}</WaitCta>
          ) : !draft.photo_id ? (
            <WaitCta>{tr('store.v1.form.waitPhoto')}</WaitCta>
          ) : missing.length ? (
            <WaitCta>{tr('store.v1.form.waitAnswers')}</WaitCta>
          ) : (
            <Cta
              label={tr('store.v1.form.sign')}
              icon="ok"
              busy={saving}
              onClick={() => void sign()}
            />
          )}
        </Footer>
      }
      overlay={
        metres !== null ? (
          <MetresSheet
            initial={metres}
            title={form.questions.find((q) => q.kind === 'number')?.[hi ? 'hi' : 'en'] ?? ''}
            onClose={() => setMetres(null)}
            onSave={(n) => {
              const q = form.questions.find((x) => x.kind === 'number')
              if (q) setAnswer(q.key, n)
              setMetres(null)
            }}
          />
        ) : undefined
      }
    >
      {inForceUntil && (
        <Strip icon="ok">{tr('store.v1.form.inForce', { date: fmtDate(inForceUntil) })}</Strip>
      )}
      {bays.length === 0 && <Strip icon="bang">{tr('store.v1.form.noBays')}</Strip>}
      <ItemRow
        padding="8px 10px"
        lead={<IconBox icon="godown" size={44} iconSize={26} />}
        title={place}
        line={tr('store.v1.form.one')}
      />
      <NumberedLine n={1} text={tr('store.v1.form.step1')} />
      <NumberedLine n={2} text={tr('store.v1.form.step2')} />
      <V1Photo
        value={draft.photo_id}
        onChange={(id) => update((d) => ({ ...d, photo_id: id }))}
        render={({ preview, busy, take }) => (
          <ItemRow
            padding="8px 10px"
            lead={
              preview ? (
                <img
                  src={preview}
                  alt=""
                  style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover' }}
                />
              ) : (
                <IconBox icon="file" />
              )
            }
            title={tr('store.v1.form.paper')}
            line={draft.photo_id ? tr('store.v1.form.taken') : undefined}
            right={
              <div style={{ flex: '0 0 auto', display: 'flex' }}>
                <Btn icon="camera" disabled={busy} onClick={take}>
                  {tr(draft.photo_id ? 'store.v1.form.again' : 'store.v1.form.take')}
                </Btn>
              </div>
            }
          />
        )}
      />
      <NumberedLine n={3} text={tr('store.v1.form.step3')} />
      {form.questions.map((q) => {
        if (q.required_if && draft.answers[q.required_if] !== true) return null
        const v = draft.answers[q.key]
        const label = hi ? q.hi : q.en
        return (
          <div key={q.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <b style={{ fontSize: 17, lineHeight: 1.3 }}>{label}</b>
            {q.kind === 'yes_no' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Answer pressed={v === true} onClick={() => setAnswer(q.key, true)}>
                  {tr('store.v1.form.yes')}
                </Answer>
                <Answer pressed={v === false} onClick={() => setAnswer(q.key, false)}>
                  {tr('store.v1.form.no')}
                </Answer>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMetres(typeof v === 'number' ? String(v) : '')}
                style={{
                  minHeight: 56,
                  borderRadius: 12,
                  border: `1.5px solid ${C.border}`,
                  background: C.surface,
                  fontSize: 22,
                  fontWeight: 700,
                  color: typeof v === 'number' ? C.ink : C.muted,
                  cursor: 'pointer',
                }}
              >
                {typeof v === 'number' ? tr('store.v1.form.metres', { n: v }) : '—'}
              </button>
            )}
          </div>
        )
      })}
    </V1Screen>
  )
}

function MetresSheet({
  initial,
  title,
  onClose,
  onSave,
}: {
  initial: string
  title: string
  onClose: () => void
  onSave: (n: number) => void
}) {
  const [v, setV] = useState(initial)
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 0, max: 5 })), [])
  return (
    <V1Sheet
      title={title}
      onClose={onClose}
      top={120}
      actions={
        v ? (
          <Cta label={tr('gate.v1.next')} icon="next" onClick={() => onSave(Number(v))} />
        ) : (
          <WaitCta>{title}</WaitCta>
        )
      }
    >
      <Readout label={title} value={v} unit="m" size={32} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}
